import React, {
  useEffect,
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
  useId,
} from "react";
import { COLORS, NEON_COLORS } from "../config/appConfig";
import "./ImageWithBoxes.css";
import { getOptimizedImageUrl, getPublicAssetUrl } from "../utils/imagePaths";

// Bounding-box area of a single SVG sub-path string (numbers are x,y pairs).
function subPathArea(subPath) {
  const nums = subPath.match(/-?\d+\.?\d*/g);
  if (!nums || nums.length < 4) return 0;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < nums.length - 1; i += 2) {
    const x = parseFloat(nums[i]);
    const y = parseFloat(nums[i + 1]);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return (maxX - minX) * (maxY - minY);
}

// Split an SVG path into sub-paths (each begins with "M"), drop tiny specks
// relative to the largest region, and rejoin. Returns the cleaned path plus the
// bounding-box area of the largest region (used for z-ordering).
function denoiseMaskPath(mask, minFraction = 0.06) {
  const subPaths = mask
    .split("M")
    .filter((chunk) => chunk.trim().length > 0)
    .map((chunk) => `M${chunk}`);
  if (subPaths.length <= 1) {
    return { d: mask, area: subPathArea(mask) || Infinity };
  }
  const areas = subPaths.map(subPathArea);
  const maxArea = Math.max(...areas) || 1;
  const kept = subPaths.filter((_, i) => areas[i] >= maxArea * minFraction);
  const cleaned = kept.length ? kept.join("") : subPaths[areas.indexOf(maxArea)];
  return { d: cleaned, area: maxArea };
}

const ImageWithBoxes = forwardRef(
  (
    {
      imageUrl,
      segments,
      onImageHover,
      onImageClick,
      hoveredId,
      noexplanation = false,
      hideOverlay = false,
      imageWidth,
      imageHeight,
    },
    ref
  ) => {
    const [paths, setPaths] = useState([]);
    const [imageSize, setImageSize] = useState(() =>
      imageWidth && imageHeight
        ? { width: imageWidth, height: imageHeight }
        : { width: 900, height: 850 }
    );
    const [displayImageUrl, setDisplayImageUrl] = useState(() => getOptimizedImageUrl(imageUrl));
    const containerRef = useRef(null);
    // Unique id per instance so the <use> reference never collides with another
    // ImageWithBoxes on the page, and so an in-place href change always repaints
    // (some WebKit builds don't repaint a <use> clone when a shared/static id's
    // href mutates in place).
    const imgId = `img-${useId().replace(/[:]/g, "")}`;
    // Segment paths are authored in image-pixel coordinates, so the SVG
    // viewBox matches the natural image size and no extra scaling is needed.
    const svgSize = imageSize;

    // Expose imperative API for boundary/hit testing
    useImperativeHandle(
      ref,
      () => ({
        getBoundingClientRect: () =>
          containerRef.current?.getBoundingClientRect(),
        getHitSegmentIdAtClientPoint: (clientX, clientY) => {
          try {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect || !paths?.length) return null;
            // Map screen point to SVG viewBox coordinates, then into the
            // (un-scaled) segment-path coordinate space.
            const svgX =
              ((clientX - rect.left) / rect.width) * svgSize.width;
            const svgY =
              ((clientY - rect.top) / rect.height) * svgSize.height;
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (!ctx) return null;
            for (const p of paths) {
              if (!p?.d || !p?.id) continue;
              try {
                const path2d = new Path2D(p.d);
                if (ctx.isPointInPath(path2d, svgX, svgY)) {
                  return p.id;
                }
              } catch (e) {
                // Ignore malformed paths
              }
            }
            return null;
          } catch (err) {
            console.warn(
              "[ImageWithBoxes] getHitSegmentIdAtClientPoint error:",
              err
            );
            return null;
          }
        },
      }),
      [paths, svgSize]
    );

    useEffect(() => {
      const optimizedUrl = getOptimizedImageUrl(imageUrl);
      const originalUrl = getPublicAssetUrl(imageUrl);

      // Always display the optimized variant for speed.
      setDisplayImageUrl(optimizedUrl);

      // Segment masks are authored in ORIGINAL-image-pixel coordinates, so the
      // SVG viewBox must match the original size. Prefer the dimensions stored
      // in the data (imageWidth/imageHeight) — this avoids downloading the
      // multi-MB original just to read its size. Only fall back to measuring
      // the original when the data doesn't provide them.
      const triggerBoundaryUpdate = () =>
        setTimeout(() => {
          if (window.getComponentBoundaries && window.updateBoundaries) {
            window.updateBoundaries();
          }
        }, 100);

      if (imageWidth && imageHeight) {
        setImageSize({ width: imageWidth, height: imageHeight });
        triggerBoundaryUpdate();
      } else {
        const sizeImg = new Image();
        sizeImg.onload = () => {
          setImageSize({
            width: sizeImg.naturalWidth,
            height: sizeImg.naturalHeight,
          });
          triggerBoundaryUpdate();
        };
        sizeImg.src = originalUrl;
      }

      // Fallback: if the optimized variant fails to load in the <image>
      // element, swap to the original URL.
      const probe = new Image();
      probe.onerror = () => setDisplayImageUrl(originalUrl);
      probe.src = optimizedUrl;
    }, [imageUrl, imageWidth, imageHeight]);

    useEffect(() => {
      function loadSegments() {
        const newPaths = segments?.map((segment, index) => {
          try {
            if (!segment.mask) {
              console.warn("No mask found for segment:", segment.id);
              return null;
            }

            const segmentId = segment.id;

            // Denoise the mask: SNMF masks are coarse patch grids that often
            // include scattered single-patch specks alongside the real region.
            // Drop sub-paths whose bounding-box area is a tiny fraction of the
            // largest one so the highlight reads as a clean blob, not confetti.
            const { d: cleanMask, area } = denoiseMaskPath(segment.mask);

            return {
              id: segmentId,
              d: cleanMask,
              color: COLORS[index % 4],
              colorIndex: index % 4,
              area,
            };
          } catch (err) {
            console.error(
              "Failed to process mask for segment:",
              segment.id,
              err
            );
            return null;
          }
        });

        const validPaths = newPaths?.filter(Boolean) || [];
        // Sort largest first → smallest on top (rendered last in SVG)
        validPaths.sort((a, b) => b.area - a.area);
        setPaths(validPaths);
      }

      loadSegments();
    }, [segments]);

    return (
      <>
        {process.env.REACT_APP_NODE_ENV === "development" && (
          <div className="image-section">
            <h1 className="section-heading">Image - {imageUrl}</h1>
          </div>
        )}
        <div
          ref={containerRef}
          className={noexplanation ? "no-explanation" : "image-container"}
          style={
            svgSize?.width && svgSize?.height
              ? { aspectRatio: `${svgSize.width} / ${svgSize.height}` }
              : undefined
          }
        >
          <svg viewBox={`0 0 ${svgSize?.width} ${svgSize?.height}`}>
            <defs>
              <image
                href={displayImageUrl}
                id={imgId}
                width={svgSize?.width}
                height={svgSize?.height}
              />
            </defs>

            <use href={`#${imgId}`} xlinkHref={`#${imgId}`} />
            {(paths || [])?.map(({ id, d, color, colorIndex }) => (
              <path
                key={id}
                d={d}
                className={noexplanation ? "no-explanation" : "hover-highlight"}
                fill="transparent"
                stroke="transparent"
                style={
                  !noexplanation
                    ? hideOverlay
                      ? {
                          stroke: "transparent",
                          fill: "transparent",
                        }
                      : {
                          stroke:
                            hoveredId === id
                              ? NEON_COLORS[colorIndex]
                              : "transparent",
                          fill: hoveredId === id ? color : "transparent",
                          strokeWidth: 10,
                          strokeLinejoin: "round",
                          strokeLinecap: "round",
                          transition: "all 0.3s ease",
                          opacity: 0.55,
                        }
                    : {}
                }
                stroke-color={NEON_COLORS[colorIndex]}
                data-segment-id={id}
                onMouseEnter={() => onImageHover?.(id)}
                onMouseLeave={() => onImageHover?.(null)}
                onClick={() => onImageClick?.(id)}
              />
            ))}
          </svg>
        </div>
      </>
    );
  }
);

ImageWithBoxes.displayName = "ImageWithBoxes";

export default ImageWithBoxes;
